import { InventoryHistoryItem } from '@/services/inventory/inventory.types'
import MAlert from '@/utils/modal/MAlert'
import useExcelDownload from '@/hooks/useDownloadExcel'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { INVENTORY_HISTORY_EXCEL_URL, toInventoryHistoryQuery } from '@/services/inventory/apis'
import { EXCEL_NO_DATA_MESSAGE } from '@/pages/inventory/inventoryHistory/consts'
import { useState } from 'react'
import { ExcelDownloadReasonModal } from '@/pages/inventory/inventoryHistory/components/ExcelDownloadReasonModal'
import { ColDef } from 'ag-grid-community'
import { HStack } from '@musinsa/mwms-ds'
import GridTable from '@/components/table/GridTable'
import { GRID_SIZE } from '@/utils/grid/grid'

interface InventoryHistoryTableProps {
  data: InventoryHistoryItem[]
  isLoading?: boolean
  downloadReasonOptions: { value: string; label: string }[]
  /** 그리드에 보이는 데이터를 조회한 조건. 엑셀은 이 조건으로 BE 에서 다시 만든다. */
  excelQuery?: ReturnType<typeof toInventoryHistoryQuery>
  onDetailSearch(item: InventoryHistoryItem): void
}
export const InventoryHistoryTable = ({ data, isLoading, onDetailSearch, downloadReasonOptions, excelQuery }: InventoryHistoryTableProps) => {
  const [isDownloadOpen, setIsDownloadOpen] = useState(false)
  const { onDownloadExcel } = useExcelDownload({ fileNamePrefix: '재고수불내역' })
  const columnDefs: ColDef<InventoryHistoryItem>[] = [
    { field: 'shipperCode', headerName: '화주코드', width: 140 },
    { field: 'clsMonth', headerName: '마감월', cellDataType: 'dateMonth', width: 120 },
    { field: 'tax', headerName: '과세', cellDataType: 'yna', width: 80 },
    { field: 'bohQty', headerName: '기초재고수량', cellDataType: 'decimal', width: 140 },
    { field: 'bohPr', headerName: '기초재고금액', cellDataType: 'integerDecimal', width: 140 },
    { field: 'anOrdQty', headerName: '입고수량', cellDataType: 'decimal', width: 140 },
    { field: 'anPr', headerName: '입고금액', cellDataType: 'integerDecimal', width: 140 },
    { field: 'anRetQty', headerName: '입고반품수량', cellDataType: 'decimal', width: 140 },
    { field: 'anRetPr', headerName: '입고반품금액', cellDataType: 'integerDecimal', width: 140 },
    { field: 'anRepQty', headerName: '입고대체수량', cellDataType: 'decimal', width: 140 },
    { field: 'anRepPr', headerName: '입고대체금액', cellDataType: 'integerDecimal', width: 140 },
    { field: 'anInQty', headerName: '입고내부거래수량', cellDataType: 'decimal', width: 140 },
    { field: 'anInPr', headerName: '입고내부거래금액', cellDataType: 'integerDecimal', width: 140 },
    { field: 'anEtcQty', headerName: '입고기타수량', cellDataType: 'decimal', width: 140 },
    { field: 'anEtcPr', headerName: '입고기타금액', cellDataType: 'integerDecimal', width: 140 },
    { field: 'doOrdQty', headerName: '출고수량', cellDataType: 'decimal', width: 140 },
    { field: 'doPr', headerName: '출고금액', cellDataType: 'integerDecimal', width: 140 },
    { field: 'doRetQty', headerName: '출고반품수량', cellDataType: 'decimal', width: 140 },
    { field: 'doRetPr', headerName: '출고반품금액', cellDataType: 'integerDecimal', width: 140 },
    { field: 'doPrQty', headerName: '출고증정수량', cellDataType: 'decimal', width: 140 },
    { field: 'doPrPr', headerName: '출고증정금액', cellDataType: 'integerDecimal', width: 140 },
    { field: 'doDesQty', headerName: '출고폐기수량', cellDataType: 'decimal', width: 140 },
    { field: 'doDesPr', headerName: '출고폐기금액', cellDataType: 'integerDecimal', width: 140 },
    { field: 'doInQty', headerName: '출고내부거래수량', cellDataType: 'decimal', width: 140 },
    { field: 'doInPr', headerName: '출고내부거래금액', cellDataType: 'integerDecimal', width: 140 },
    { field: 'doEtcQty', headerName: '출고기타수량', cellDataType: 'decimal', width: 140 },
    { field: 'doEtcPr', headerName: '출고기타금액', cellDataType: 'integerDecimal', width: 140 },
    { field: 'unEohQty', headerName: '기말미확정재고수량', cellDataType: 'decimal', width: 140 },
    { field: 'unEohPr', headerName: '기말미확정재고금액', cellDataType: 'integerDecimal', width: 140 },
    { field: 'eohQty', headerName: '기말확정재고수량', cellDataType: 'decimal', width: 140 },
    { field: 'eohPr', headerName: '기말확정재고금액', cellDataType: 'integerDecimal', width: 140 },
  ]
  return (
    <HStack full gap={16}>
      <GridTable<InventoryHistoryItem>
        gridKey="IV02_HEAD"
        rowData={data}
        isLoading={isLoading}
        countPerPage={GRID_SIZE.DEFAULT}
        columnDefs={columnDefs}
        totalCount={data.length}
        useColumnFilter
        onExportData={() => {
          if (!data.length || !excelQuery) {
            MAlert.show(EXCEL_NO_DATA_MESSAGE)
            return
          }
          setIsDownloadOpen(true)
        }}
        onRowDoubleClicked={(event) => event.data && onDetailSearch(event.data)}
      />
      <ExcelDownloadReasonModal
        open={isDownloadOpen}
        reasonOptions={downloadReasonOptions}
        onConfirm={({ reasonCode, reasonDetail }) => {
          setIsDownloadOpen(false)
          // 감사로그(EXPORT)는 BE 가 쿼리의 reasonCode·detailedReason 으로 남긴다(LOGISPRD-13945).
          // qsStringify 가 encode:false 라 자유입력 사유는 직접 인코딩한다 — 안 하면 `&`·`#` 에서 사유가 잘린다.
          // 엑셀 컬럼 구성은 조회 대상 화주가 아니라 로그인 세션 화주로 정해진다(ST02 와 같은 계약).
          onDownloadExcel(INVENTORY_HISTORY_EXCEL_URL, { ...excelQuery, viewerShipperCode: AuthStorage.user.get().SPR_NM ?? '', reasonCode: encodeURIComponent(reasonCode), detailedReason: encodeURIComponent(reasonDetail) })
        }}
        onCancel={() => setIsDownloadOpen(false)}
      />
    </HStack>
  )
}
